import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LifeBuoy, Search, RefreshCw, Loader2, Send, Mail, User, Clock, Lock,
  ShieldCheck, ExternalLink, UserCircle2, Circle, Coins, ArrowUpRight,
  ArrowDownLeft, Ban, Users, Bell, PhoneCall, Trophy, Award,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import useSupportTicketLive from "@/hooks/useSupportTicketLive";
import DemoBanner from "@/components/DemoBanner";

/**
 * AdminSupport — live-chat support console for staff.
 *
 * Layout (desktop, three columns):
 *   [ ticket list ] [ live chat conversation ] [ user context ]
 *
 * A single WebSocket per selected ticket keeps messages, typing indicators,
 * presence, and claim events in perfect sync between staff + the ticket owner.
 * If the socket drops we transparently reconnect and refetch.
 */

const STATUS_META = {
  open:     { label: "Open",          color: "cyan",   dot: "var(--sc-cyan)" },
  pending:  { label: "Awaiting user", color: "orange", dot: "var(--sc-orange)" },
  resolved: { label: "Resolved",      color: "volt",   dot: "var(--sc-volt)" },
  closed:   { label: "Closed",        color: "pink",   dot: "#888" },
};

// Priority = submitter's subscription tier at ticket-creation time.
// Diamond tickets always float to the top of the queue.
const PRIORITY_META = {
  3: { label: "DIAMOND", color: "#00E5FF", bg: "rgba(0,229,255,0.15)" },
  2: { label: "GOLD",    color: "#FFD700", bg: "rgba(255,215,0,0.15)" },
  1: { label: "BRONZE",  color: "#C08457", bg: "rgba(192,132,87,0.15)" },
};

function PriorityBadge({ priority, size = "sm" }) {
  const m = PRIORITY_META[priority] || PRIORITY_META[1];
  const cls = size === "lg" ? "text-[11px] px-2 py-0.5" : "text-[9px] px-1.5 py-0.5";
  return (
    <span
      className={`sc-mono font-black rounded ${cls}`}
      style={{ color: m.color, background: m.bg, border: `1px solid ${m.color}55` }}
      data-testid={`priority-badge-${m.label.toLowerCase()}`}
      title={`${m.label} priority`}
    >
      {m.label}
    </span>
  );
}

const wsUrlFor = (ticketId, jwt) => {
  const backend = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const proto = backend.startsWith("https") ? "wss" : "ws";
  const host = backend.replace(/^https?:\/\//, "");
  return `${proto}://${host}/api/support/ws/${ticketId}?jwt=${encodeURIComponent(jwt)}`;
};

export default function AdminSupport() {
  const { user } = useAuth();
  const nav = useNavigate();

  // List state
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [q, setQ] = useState("");
  // View: "active" (open + pending), "closed" (resolved + closed), or
  // "leaderboard" (team performance page). Default to active queue.
  const [view, setView] = useState("active");
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);

  // Selection + detail
  const [selectedId, setSelectedId] = useState(null);
  const [ticket, setTicket] = useState(null);
  const [context, setContext] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Race-guard: any in-flight loadDetail / user-context request that resolves
  // AFTER the user has already switched to a different ticket must be
  // dropped, otherwise the detail pane visibly "jumps back" to the ticket
  // the request was originally for.
  const selectedIdRef = useRef(null);
  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);

  // Preserve the ticket-list scroll position across silent refreshes. React
  // reconciliation will normally keep scrollTop stable when children are
  // memoized with stable keys, but the container itself can re-mount when
  // switching views, so we snapshot on scroll and re-apply after refresh.
  const listScrollRef = useRef(null);
  const listScrollTopRef = useRef(0);

  // Live chat
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [presence, setPresence] = useState({ user_online: false, staff_online: false, staff_names: [] });
  const [typing, setTyping] = useState({ user: false, staff: false });
  const [connState, setConnState] = useState("idle");
  const wsRef = useRef(null);
  const scrollRef = useRef(null);
  const typingTimer = useRef(null);
  const notifyAudio = useRef(null);

  // Client-side admin guard. Demo (buyer-preview) accounts are allowed
  // through — the backend still 403s every write for them, and the GET
  // responses are scrubbed to a fresh-slate zero state.
  useEffect(() => {
    if (user && !user.is_admin && !user.is_demo) {
      toast.error("Admin access only.");
      nav("/");
    }
  }, [user, nav]);

  // ---------- Load list ----------
  // `silent` skips the "loading" spinner + skips setState if payload is
  // identical to what we already have — this is what stops the list from
  // visually jumping / flickering on the auto-refresh interval.
  const listSigRef = useRef("");
  const loadList = useCallback((opts = {}) => {
    const { silent = false } = opts;
    if (view === "leaderboard") return;
    if (!silent) setLoading(true);
    const params = { view };
    if (statusFilter) params.status = statusFilter;
    if (categoryFilter) params.category = categoryFilter;
    if (q.trim()) params.q = q.trim();
    api.get("/admin/support/tickets", { params })
      .then((r) => {
        const nextTickets = r.data.tickets || [];
        // Cheap signature: id+status+updated_at per row + total. If unchanged,
        // do not re-set React state (prevents pointless list re-renders).
        const sig = nextTickets.map((t) => `${t.id}:${t.status}:${t.updated_at}:${t.priority}:${t.claimed_by}:${t.closed_by}`).join("|") + `#${r.data.total}`;
        if (sig !== listSigRef.current) {
          listSigRef.current = sig;
          setTickets(nextTickets);
          setCounts(r.data.counts || {});
          setTotal(r.data.total || 0);
        }
      })
      .catch((e) => {
        // Silent auto-refresh failures should not toast on every interval —
        // it made the panel feel constantly "angry" on flaky connections.
        if (silent) return;
        const d = e?.response?.data?.detail;
        toast.error(typeof d === "string" ? d : "Could not load tickets");
      })
      .finally(() => { if (!silent) setLoading(false); });
  }, [view, statusFilter, categoryFilter, q]);

  // ---------- Load leaderboard ----------
  const loadLeaderboard = useCallback(() => {
    setLeaderboardLoading(true);
    api.get("/admin/support/leaderboard")
      .then((r) => setLeaderboard(r.data.leaderboard || []))
      .catch(() => setLeaderboard([]))
      .finally(() => setLeaderboardLoading(false));
  }, []);

  useEffect(() => { if (view === "leaderboard") loadLeaderboard(); }, [view, loadLeaderboard]);

  // Load list on filter/view change — with a debounce on the search input so
  // typing doesn't fire a request on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => loadList(), q ? 300 : 0);
    return () => clearTimeout(t);
  }, [loadList, q]);

  // Auto-refresh list every 30s silently (no spinner, no toast on failure,
  // no re-render if payload is unchanged). Was 20s + full spinner which
  // caused the visible "jump" the user reported.
  useEffect(() => {
    const t = setInterval(() => loadList({ silent: true }), 30000);
    return () => clearInterval(t);
  }, [loadList]);

  // ---------- Load selected ticket + context ----------
  // Skip state replacement when the ticket payload is byte-identical to what
  // we already have on screen. This is what stops the detail pane + chat
  // scroll from flickering on every WS `onSync`.
  const detailSigRef = useRef("");
  const contextSigRef = useRef("");
  const loadDetail = useCallback((id, opts = {}) => {
    const { silent = false } = opts;
    if (!silent) setDetailLoading(true);
    api.get(`/admin/support/tickets/${id}`)
      .then((r) => {
        // Race-guard: drop the response if the admin already moved on.
        if (selectedIdRef.current !== id) return;
        const t = r.data;
        const sig = `${t.id}:${t.status}:${t.updated_at}:${(t.messages || []).length}:${t.claimed_by}:${t.closed_by}`;
        if (sig !== detailSigRef.current) {
          detailSigRef.current = sig;
          setTicket(t);
        }
      })
      .catch((e) => {
        if (silent) return;
        if (selectedIdRef.current !== id) return;
        const d = e?.response?.data?.detail;
        toast.error(typeof d === "string" ? d : "Could not load ticket");
      })
      .finally(() => { if (!silent && selectedIdRef.current === id) setDetailLoading(false); });
    api.get(`/admin/support/tickets/${id}/user-context`)
      .then((r) => {
        if (selectedIdRef.current !== id) return;
        const sig = JSON.stringify(r.data || {}).slice(0, 400);
        if (sig !== contextSigRef.current) {
          contextSigRef.current = sig;
          setContext(r.data);
        }
      })
      .catch(() => { if (selectedIdRef.current === id) setContext(null); });
  }, []);

  const selectTicket = useCallback((t) => {
    setSelectedId((prev) => {
      if (prev === t.id) return prev;
      detailSigRef.current = "";
      contextSigRef.current = "";
      loadDetail(t.id);
      return t.id;
    });
  }, [loadDetail]);

  // ---------- Live WebSocket per selected ticket (auto-reconnect + poll fallback) ----------
  const jwt = typeof window !== "undefined" ? localStorage.getItem("sc_token") : null;
  const wsUrl = selectedId && jwt ? wsUrlFor(selectedId, jwt) : null;

  const { send: sendWs } = useSupportTicketLive({
    enabled: !!selectedId,
    wsUrl,
    onStateChange: setConnState,
    onSync: useCallback(() => {
      // WS says "state changed, resync" — do this silently so we don't
      // flash the detail loading spinner mid-conversation.
      if (selectedId) loadDetail(selectedId, { silent: true });
    }, [selectedId, loadDetail]),
    onMessage: useCallback((message) => {
      setTicket((t) => t ? { ...t, messages: [...(t.messages || []), message] } : t);
      if (message?.from === "user") {
        try { notifyAudio.current?.play?.().catch(() => {}); } catch (_e) { /* ignore */ }
      }
    }, []),
    onPresence: useCallback((p) => {
      setPresence({ user_online: p.user_online, staff_online: p.staff_online, staff_names: p.staff_names || [] });
    }, []),
    onTyping: useCallback((p) => {
      if (p.who === "user") setTyping((t) => ({ ...t, user: p.is_typing }));
    }, []),
    onClaim: useCallback((p) => {
      setTicket((t) => t ? { ...t, claimed_by: p.claimed_by } : t);
    }, []),
  });
  useEffect(() => { wsRef.current = { send: sendWs }; }, [sendWs]);

  // Autoscroll on new message.
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [ticket?.messages?.length, typing.user]);

  // ---------- Actions ----------
  const sendTyping = (isTyping) => {
    if (!wsRef.current?.send) return;
    wsRef.current.send({ type: "typing", is_typing: isTyping });
  };
  const onReplyChange = (v) => {
    setReply(v);
    sendTyping(true);
    if (typingTimer.current) clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => sendTyping(false), 1500);
  };

  const sendReply = async (e) => {
    e?.preventDefault?.();
    if (!reply.trim() || sending || !selectedId) return;
    setSending(true);
    try {
      await api.post(`/admin/support/tickets/${selectedId}/reply`, { message: reply.trim() });
      setReply("");
      sendTyping(false);
      // The message will arrive back over the WS. Refresh the list so status flips visually.
      loadList();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Could not send reply.");
    } finally { setSending(false); }
  };

  const setStatus = async (newStatus) => {
    if (!selectedId) return;
    try {
      await api.patch(`/admin/support/tickets/${selectedId}/status`, { status: newStatus });
      loadDetail(selectedId);
      loadList();
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Could not update status"); }
  };

  const claim = async () => {
    if (!selectedId) return;
    try { await api.post(`/admin/support/tickets/${selectedId}/claim`); loadDetail(selectedId); loadList(); }
    catch (e2) { toast.error(e2?.response?.data?.detail || "Could not claim"); }
  };
  const unclaim = async () => {
    if (!selectedId) return;
    try { await api.post(`/admin/support/tickets/${selectedId}/unclaim`); loadDetail(selectedId); loadList(); }
    catch (e2) { toast.error(e2?.response?.data?.detail || "Could not unclaim"); }
  };

  const claimedByMe = ticket?.claimed_by && user && ticket.claimed_by === user.username;

  // Show a small notice when the currently-open ticket is no longer part of
  // the tab we're viewing (e.g. someone else closed it while it was open, or
  // the admin switched tabs). We deliberately do NOT auto-clear the detail
  // pane — that used to cause the "jumping" the user reported. Instead they
  // see a friendly note and can decide to switch tabs themselves.
  const ticketNotInQueue = !!(
    ticket && selectedId && !loading &&
    !tickets.some((t) => t.id === selectedId)
  );

  // Preserve list scrollTop across silent refreshes. After tickets state is
  // updated we restore the previous scroll position on the very next frame.
  useEffect(() => {
    const el = listScrollRef.current;
    if (!el) return;
    const target = listScrollTopRef.current;
    if (target > 0 && Math.abs(el.scrollTop - target) > 2) {
      // rAF so it lands after the browser has laid out the new rows.
      requestAnimationFrame(() => {
        if (listScrollRef.current) listScrollRef.current.scrollTop = target;
      });
    }
  }, [tickets]);

  return (
    <div className="min-h-screen bg-[#0A0A0C]" data-testid="admin-support">
      <audio ref={notifyAudio} src="/alerts/ding.mp3" preload="auto" />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6">
        <DemoBanner />
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <LifeBuoy className="w-4 h-4" style={{ color: "var(--sc-cyan)" }} />
              <span className="sc-label" style={{ color: "var(--sc-cyan)" }}>ADMIN · LIVE SUPPORT</span>
            </div>
            <h1 className="sc-heading text-2xl sm:text-3xl font-black tracking-tight">Support Console</h1>
            <div className="sc-mono text-xs text-zinc-500 mt-1">
              {total} · <span className="text-[color:var(--sc-cyan)]">{counts.open || 0} open</span> · <span className="text-[color:var(--sc-orange)]">{counts.pending || 0} pending</span> · <span className="text-[color:var(--sc-volt)]">{counts.resolved || 0} resolved</span>
            </div>
          </div>
          <button onClick={loadList} className="sc-btn-ghost text-xs flex items-center gap-1.5" data-testid="admin-support-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        {/* View tabs — active queue / closed archive / leaderboard */}
        <div className="flex items-center gap-1 mb-3 border-b border-white/5">
          {[
            { id: "active",       label: "Active Queue",  count: (counts.open || 0) + (counts.pending || 0) },
            { id: "closed",       label: "Closed",        count: (counts.resolved || 0) + (counts.closed || 0) },
            { id: "leaderboard",  label: "Team Leaderboard", count: null },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setView(tab.id)}
              className={`px-4 py-2 text-xs sc-mono uppercase tracking-widest border-b-2 -mb-px transition ${
                view === tab.id
                  ? "border-[color:var(--sc-cyan)] text-white"
                  : "border-transparent text-zinc-500 hover:text-zinc-300"
              }`}
              data-testid={`admin-support-tab-${tab.id}`}
            >
              {tab.label}
              {tab.count !== null && (
                <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-zinc-400">{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Filters (hidden on leaderboard view) */}
        {view !== "leaderboard" && (
        <div className="grid sm:grid-cols-[1fr_auto_auto] gap-2 mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
            <input type="search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search subject, email, or message…" data-testid="admin-support-search"
              className="w-full pl-9 pr-3 py-2 bg-[#0F0F14] border border-white/10 focus:border-[color:var(--sc-cyan)] focus:outline-none rounded sc-mono text-sm text-zinc-100 placeholder:text-zinc-600" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-[#0F0F14] border border-white/10 rounded sc-mono text-xs" data-testid="admin-support-status-filter">
            <option value="">All statuses</option>
            <option value="open">Open</option>
            <option value="pending">Awaiting user</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 bg-[#0F0F14] border border-white/10 rounded sc-mono text-xs" data-testid="admin-support-category-filter">
            <option value="">All categories</option>
            <option value="support">General</option>
            <option value="billing">Billing</option>
            <option value="safety">Trust &amp; Safety</option>
            <option value="privacy">Privacy</option>
            <option value="dmca">DMCA</option>
            <option value="legal">Legal</option>
            <option value="press">Press</option>
            <option value="bug">Bug</option>
          </select>
        </div>
        )}

        {/* Leaderboard view */}
        {view === "leaderboard" && (
          <LeaderboardPanel rows={leaderboard} loading={leaderboardLoading} onRefresh={loadLeaderboard} />
        )}

        {/* 3-column live console (hidden on leaderboard view) */}
        {view !== "leaderboard" && (
        <div className="grid lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)_minmax(0,340px)] gap-3">
          {/* ---------- Left: ticket list ---------- */}
          <div
            ref={listScrollRef}
            onScroll={(e) => { listScrollTopRef.current = e.currentTarget.scrollTop; }}
            className="term-card p-2 h-[76vh] mc-scroll-neon space-y-1"
            style={{ overflowY: "scroll", overflowX: "hidden" }}
            data-testid="admin-support-list"
          >
            {loading && <div className="text-center py-10"><Loader2 className="w-5 h-5 mx-auto animate-spin text-zinc-500" /></div>}
            {!loading && tickets.length === 0 && (
              <div className="text-center py-10 text-xs text-zinc-500">
                <LifeBuoy className="w-6 h-6 mx-auto mb-2 text-zinc-700" />
                Inbox zero — nice.
              </div>
            )}
            {tickets.map((t) => (
              <TicketRow
                key={t.id}
                t={t}
                active={t.id === selectedId}
                onClick={selectTicket}
              />
            ))}
          </div>

          {/* ---------- Center: live conversation ---------- */}
          <div className="term-card p-0 flex flex-col max-h-[76vh]" data-testid="admin-support-chat">
            {!ticket && !detailLoading && (
              <div className="flex-1 grid place-items-center text-center px-6" data-testid="admin-support-empty">
                <div>
                  <LifeBuoy className="w-10 h-10 mx-auto mb-3 text-zinc-700" />
                  <div className="sc-heading text-sm text-zinc-400">Select a ticket to start the conversation.</div>
                </div>
              </div>
            )}
            {detailLoading && <div className="flex-1 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>}
            {ticket && !detailLoading && (
              <>
                <ChatHeader
                  ticket={ticket} presence={presence} connState={connState}
                  claimedByMe={claimedByMe} onClaim={claim} onUnclaim={unclaim} onStatus={setStatus}
                />
                {ticketNotInQueue && (
                  <div
                    className="px-3 py-2 border-b border-white/5 flex items-center gap-2 text-[11px] text-zinc-400 bg-[rgba(255,89,0,0.06)]"
                    data-testid="admin-ticket-not-in-queue-notice"
                  >
                    <Bell className="w-3 h-3 text-[color:var(--sc-orange)] shrink-0" />
                    <span>
                      This ticket is no longer in the <span className="text-white font-bold">{view === "active" ? "Active" : "Closed"}</span> queue
                      {ticket.status ? <> — its status is now <span className="text-white font-bold">{STATUS_META[ticket.status]?.label || ticket.status}</span>.</> : "."}
                      {" "}You can still view or reply below, or switch tabs to find it.
                    </span>
                  </div>
                )}
                <div ref={scrollRef} className="flex-1 mc-scroll-neon p-4 space-y-2 bg-[#0B0B10]/60" style={{ overflowY: "auto" }}>
                  {(ticket.messages || []).map((m) => <Bubble key={m.id} msg={m} />)}
                  {typing.user && <TypingIndicator name={ticket.email} />}
                </div>
                {ticket.status !== "closed" ? (
                  <form onSubmit={sendReply} className="p-3 border-t border-white/5 flex items-end gap-2" data-testid="admin-ticket-reply-form">
                    <textarea
                      value={reply}
                      onChange={(e) => onReplyChange(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(e); } }}
                      placeholder="Reply as staff… (Shift+Enter for newline)"
                      rows={2} maxLength={4000}
                      className="flex-1 px-3 py-2 bg-[#0F0F14] border border-white/10 focus:border-[color:var(--sc-cyan)] focus:outline-none rounded sc-mono text-sm text-zinc-100 placeholder:text-zinc-600 resize-none"
                      data-testid="admin-ticket-reply-input"
                    />
                    <button type="submit" disabled={sending || reply.trim().length < 1}
                      className="sc-btn text-xs flex items-center gap-1.5 disabled:opacity-60 h-[42px]"
                      data-testid="admin-ticket-reply-submit">
                      {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                      Send
                    </button>
                  </form>
                ) : (
                  <div className="p-4 border-t border-white/5 text-center text-xs text-zinc-500 flex items-center justify-center gap-2">
                    <Lock className="w-3.5 h-3.5" /> Ticket closed. Reopen to reply.
                  </div>
                )}
              </>
            )}
          </div>

          {/* ---------- Right: user context ---------- */}
          <div
            className="h-[76vh] mc-scroll-neon"
            style={{ overflowY: "auto", overflowX: "hidden" }}
            data-testid="admin-support-context"
          >
            {!ticket && <div className="term-card p-6 text-center text-xs text-zinc-500">Pick a ticket to see the user profile.</div>}
            {ticket && <UserContextPanel context={context} ticket={ticket} />}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Ticket row
// -----------------------------------------------------------------------------
function TicketRowRaw({ t, active, onClick }) {
  const meta = STATUS_META[t.status] || STATUS_META.open;
  const lastMsg = (t.messages || [])[t.messages.length - 1];
  const handleClick = () => onClick(t);
  return (
    <button type="button" onClick={handleClick}
      className={`w-full text-left p-2.5 rounded border transition ${
        active ? "border-[color:var(--sc-cyan)] bg-[rgba(0,240,255,0.06)]" : "border-white/10 hover:border-white/25 hover:bg-white/5"
      }`}
      data-testid={`admin-ticket-row-${t.id}`}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="sc-heading text-xs font-bold truncate flex-1">{t.subject}</div>
        <PriorityBadge priority={t.priority} />
        <span className="sc-mono text-[9px] px-1.5 py-0.5 rounded flex items-center gap-1"
          style={{ background: `${meta.dot}22`, color: meta.dot }}>
          <span className="w-1 h-1 rounded-full" style={{ background: meta.dot }} />
          {meta.label}
        </span>
      </div>
      <div className="sc-mono text-[10px] text-zinc-500 truncate">{t.email}</div>
      {lastMsg && <div className="text-[11px] text-zinc-400 truncate mt-1">{lastMsg.from === "staff" ? "You: " : ""}{lastMsg.body}</div>}
      <div className="sc-mono text-[10px] text-zinc-600 mt-1 flex items-center gap-2 flex-wrap">
        <span>#{t.id.slice(0, 6).toUpperCase()}</span>
        <span>·</span>
        <span>{new Date(t.updated_at || t.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
        {t.claimed_by && <><span>·</span><span className="text-[color:var(--sc-volt)]" data-testid={`ticket-claimed-by-${t.id}`}>Handled by @{t.claimed_by}</span></>}
        {t.closed_by && !t.claimed_by && <><span>·</span><span className="text-zinc-500" data-testid={`ticket-closed-by-${t.id}`}>Closed by @{t.closed_by}</span></>}
      </div>
    </button>
  );
}

// Memoize so unchanged rows don't re-render during the auto-refresh cycle.
// Uses a shallow content comparator on the fields the row actually renders.
const TicketRow = React.memo(TicketRowRaw, (prev, next) => (
  prev.active === next.active &&
  prev.onClick === next.onClick &&
  prev.t.id === next.t.id &&
  prev.t.status === next.t.status &&
  prev.t.priority === next.t.priority &&
  prev.t.updated_at === next.t.updated_at &&
  prev.t.claimed_by === next.t.claimed_by &&
  prev.t.closed_by === next.t.closed_by &&
  ((prev.t.messages || []).length === (next.t.messages || []).length)
));

// -----------------------------------------------------------------------------
// Chat header (subject + status + claim + presence)
// -----------------------------------------------------------------------------
function ChatHeader({ ticket, presence, connState, claimedByMe, onClaim, onUnclaim, onStatus }) {
  const meta = STATUS_META[ticket.status] || STATUS_META.open;
  return (
    <div className="p-3 border-b border-white/5 flex items-center gap-3 flex-wrap">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div className="sc-heading text-sm font-bold truncate">{ticket.subject}</div>
          <span className="sc-mono text-[9px] px-1.5 py-0.5 rounded flex items-center gap-1 shrink-0"
            style={{ background: `${meta.dot}22`, color: meta.dot }}>
            <span className="w-1 h-1 rounded-full" style={{ background: meta.dot }} />
            {meta.label}
          </span>
        </div>
        <div className="text-[10px] sc-mono text-zinc-500 flex items-center gap-2 mt-0.5">
          <span>#{ticket.id.slice(0, 6).toUpperCase()}</span><span>·</span>
          <span>{ticket.email}</span>
          <span>·</span>
          <Circle className={`w-2 h-2 fill-current ${presence.user_online ? "text-[color:var(--sc-volt)]" : "text-zinc-700"}`} />
          <span>{presence.user_online ? "user online" : "user offline"}</span>
          <span>·</span>
          <span className={connState === "connected" ? "text-[color:var(--sc-volt)]" : "text-zinc-500"} data-testid="admin-support-ws-state">
            {connState === "connected" ? "live" : connState}
          </span>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        {ticket.claimed_by ? (
          <button onClick={onUnclaim} className="sc-btn-ghost text-[10px] flex items-center gap-1" data-testid="admin-ticket-unclaim">
            <ShieldCheck className="w-3 h-3 text-[color:var(--sc-volt)]" /> {claimedByMe ? "Unclaim (yours)" : `Claimed by @${ticket.claimed_by}`}
          </button>
        ) : (
          <button onClick={onClaim} className="sc-btn text-[10px] flex items-center gap-1" data-testid="admin-ticket-claim">
            <ShieldCheck className="w-3 h-3" /> Claim
          </button>
        )}
        <select value={ticket.status} onChange={(e) => onStatus(e.target.value)}
          className="px-2 py-1 bg-[#0F0F14] border border-white/10 rounded sc-mono text-[10px] text-zinc-100"
          data-testid="admin-ticket-status-select">
          <option value="open">Open</option>
          <option value="pending">Awaiting user</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
      </div>
    </div>
  );
}

function Bubble({ msg }) {
  const isStaff = msg.from === "staff";
  return (
    <div className={`flex ${isStaff ? "justify-end" : "justify-start"}`} data-testid={`msg-${msg.id}`}>
      <div className={`max-w-[80%] p-2.5 rounded-lg border ${isStaff ? "border-[color:var(--sc-cyan)]/40 bg-[rgba(0,240,255,0.08)]" : "border-white/10 bg-white/[0.03]"}`}>
        <div className="flex items-center gap-2 mb-1">
          <span className="sc-mono text-[9px] font-bold" style={{ color: isStaff ? "var(--sc-cyan)" : "#a1a1aa" }}>
            {isStaff ? "STAFF" : "USER"} · {msg.author}
          </span>
          <span className="sc-mono text-[10px] text-zinc-600">{new Date(msg.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
        </div>
        <div className="text-sm text-zinc-100 whitespace-pre-wrap leading-relaxed">{msg.body}</div>
      </div>
    </div>
  );
}

function TypingIndicator({ name }) {
  return (
    <div className="flex justify-start" data-testid="typing-indicator">
      <div className="px-3 py-2 rounded-lg border border-white/10 bg-white/[0.02] flex items-center gap-1.5">
        <span className="text-[10px] sc-mono text-zinc-500">{name} is typing</span>
        <span className="flex gap-0.5">
          <span className="w-1.5 h-1.5 rounded-full bg-zinc-500 animate-bounce" style={{ animationDelay: "0ms" }} />
          <span className="w-1.5 h-1.5 rounded-full bg-zinc-500 animate-bounce" style={{ animationDelay: "120ms" }} />
          <span className="w-1.5 h-1.5 rounded-full bg-zinc-500 animate-bounce" style={{ animationDelay: "240ms" }} />
        </span>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// User context sidebar
// -----------------------------------------------------------------------------
function UserContextPanel({ context, ticket }) {
  if (context === null) return <div className="term-card p-4 text-xs text-zinc-500">Loading user context…</div>;
  if (!context?.match) {
    return (
      <div className="term-card p-4 text-xs text-zinc-400 space-y-2" data-testid="context-anonymous">
        <div className="sc-label text-[10px]" style={{ color: "var(--sc-orange)" }}>◆ ANONYMOUS TICKET</div>
        <p>No MystiCast account matches <span className="sc-mono text-zinc-200">{ticket.email}</span>.</p>
        <p className="text-[11px] text-zinc-500">This is a guest submission. Replies still land in the ticket view URL.</p>
      </div>
    );
  }
  const u = context.user;

  return (
    <div className="space-y-3" data-testid="context-user">
      {/* Identity */}
      <div className="term-card p-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[color:var(--sc-volt)] to-[color:var(--sc-cyan)] grid place-items-center text-black font-black text-lg overflow-hidden">
            {u.avatar_url ? <img src={u.avatar_url} alt="" className="w-full h-full object-cover" /> : u.username[0].toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="sc-heading text-sm font-black truncate">{u.username}</div>
            <div className="text-[10px] sc-mono text-zinc-500 truncate">{u.email}</div>
          </div>
          <a href={`/u/${u.username}`} target="_blank" rel="noopener noreferrer"
            className="p-1.5 rounded hover:bg-white/5 text-zinc-500 hover:text-[color:var(--sc-cyan)]" data-testid="context-open-profile">
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5 text-[9px] sc-mono">
          <Pill label={u.tier?.toUpperCase() || "FREE"} color="cyan" />
          {u.is_verified_streamer && <Pill label="STREAMER" color="pink" />}
          {u.is_admin && <Pill label={(u.role || "ADMIN").toUpperCase()} color="volt" />}
          {u.kyc_status && <Pill label={`KYC · ${u.kyc_status.toUpperCase()}`} color={u.kyc_status === "approved" ? "volt" : "orange"} />}
          {u.banned && <Pill label="BANNED" color="orange" />}
        </div>
        <div className="grid grid-cols-2 gap-2 mt-3 text-[10px] sc-mono">
          <MiniStat icon={Coins} label="GC" value={u.gold_coins?.toLocaleString?.() ?? 0} color="orange" />
          <MiniStat icon={Coins} label="SPARKS" value={u.sparks?.toLocaleString?.() ?? 0} color="cyan" />
          <MiniStat icon={Users} label="FOLLOWERS" value={context.counts.followers} color="pink" />
          <MiniStat icon={UserCircle2} label="FOLLOWING" value={context.counts.follows} color="volt" />
          <MiniStat icon={ShieldCheck} label="SUBS" value={context.counts.active_subs} color="volt" />
          <MiniStat icon={Ban} label="BANS" value={context.counts.bans_against} color="orange" />
        </div>
      </div>

      {/* Ledger transactions */}
      <div className="term-card p-4">
        <div className="sc-label mb-2" style={{ color: "var(--sc-cyan)" }}>◆ RECENT TRANSACTIONS</div>
        {context.recent_ledger.length === 0 ? (
          <div className="text-xs text-zinc-500 text-center py-3">No transactions recorded.</div>
        ) : (
          <ul className="space-y-1.5" data-testid="context-transactions">
            {context.recent_ledger.slice(0, 8).map((r) => {
              const isEarn = r.streamer_username === u.username && r.streamer_share_cents > 0;
              const amt = isEarn ? r.streamer_share_cents : r.gross_usd_cents;
              return (
                <li key={r.id} className="flex items-start gap-2 text-[11px] py-1 border-b border-white/5 last:border-0">
                  {isEarn ? <ArrowDownLeft className="w-3 h-3 mt-0.5 text-[color:var(--sc-volt)] shrink-0" />
                          : <ArrowUpRight   className="w-3 h-3 mt-0.5 text-[color:var(--sc-orange)] shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <div className="text-zinc-300">{r.type}</div>
                    <div className="sc-mono text-[9px] text-zinc-600">{new Date(r.at).toLocaleString()}</div>
                  </div>
                  <div className="sc-mono font-bold shrink-0" style={{ color: isEarn ? "var(--sc-volt)" : "var(--sc-orange)" }}>
                    {isEarn ? "+" : "-"}${(amt / 100).toFixed(2)}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Active subscriptions */}
      {context.active_subs.length > 0 && (
        <div className="term-card p-4">
          <div className="sc-label mb-2" style={{ color: "var(--sc-pink)" }}>◆ ACTIVE SUBS</div>
          <ul className="space-y-1.5 text-[11px]" data-testid="context-subs">
            {context.active_subs.map((s) => (
              <li key={s.channel_owner_username + s.started_at} className="flex items-center justify-between border-b border-white/5 last:border-0 py-1">
                <span className="text-zinc-300 truncate">@{s.channel_owner_username}</span>
                <span className="sc-mono text-[10px] text-zinc-500">{s.source} · exp {new Date(s.expires_at).toLocaleDateString()}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Other tickets by same user */}
      {context.other_tickets.length > 0 && (
        <div className="term-card p-4">
          <div className="sc-label mb-2" style={{ color: "var(--sc-orange)" }}>◆ OTHER TICKETS ({context.other_tickets.length})</div>
          <ul className="space-y-1.5 text-[11px]" data-testid="context-other-tickets">
            {context.other_tickets.map((t) => (
              <li key={t.id} className="flex items-center justify-between border-b border-white/5 last:border-0 py-1">
                <span className="text-zinc-300 truncate flex-1">{t.subject}</span>
                <span className="sc-mono text-[9px] shrink-0" style={{ color: STATUS_META[t.status]?.dot }}>
                  {STATUS_META[t.status]?.label}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Pill({ label, color }) {
  return (
    <span className="px-1.5 py-0.5 rounded font-bold" style={{ background: `var(--sc-${color})22`, color: `var(--sc-${color})` }}>
      {label}
    </span>
  );
}
function MiniStat({ icon: Icon, label, value, color }) {
  return (
    <div className="p-2 border border-white/5 rounded" style={{ borderColor: `var(--sc-${color})30` }}>
      <div className="flex items-center gap-1 sc-label text-[9px]" style={{ color: `var(--sc-${color})` }}>
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="sc-heading text-sm font-black mt-0.5">{value}</div>
    </div>
  );
}


// -----------------------------------------------------------------------------
// LeaderboardPanel — team performance view. Ranks agents by resolved tickets,
// tiebreak by active claims, then alphabetical username. Top 3 get medal chips.
// -----------------------------------------------------------------------------
function LeaderboardPanel({ rows, loading, onRefresh }) {
  const medals = ["#FFD700", "#C0C0C0", "#CD7F32"]; // gold, silver, bronze
  return (
    <div className="term-card p-6" data-testid="admin-support-leaderboard">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Trophy className="w-4 h-4 text-[#FFD700]" />
            <span className="sc-label" style={{ color: "#FFD700" }}>TEAM LEADERBOARD</span>
          </div>
          <h2 className="sc-heading text-2xl font-black">Support Agent Standings</h2>
          <p className="text-xs text-zinc-500 mt-1">Ranked by tickets resolved. Active claims are tickets currently in-flight.</p>
        </div>
        <button onClick={onRefresh} className="sc-btn-ghost text-xs flex items-center gap-1.5" data-testid="admin-support-leaderboard-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {loading && (
        <div className="text-center py-10"><Loader2 className="w-5 h-5 mx-auto animate-spin text-zinc-500" /></div>
      )}

      {!loading && rows.length === 0 && (
        <div className="text-center py-14 text-sm text-zinc-500" data-testid="leaderboard-empty">
          <Trophy className="w-8 h-8 mx-auto mb-3 text-zinc-700" />
          No resolved tickets yet. Once your team starts closing them, they&apos;ll appear here.
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="space-y-2" data-testid="leaderboard-list">
          {rows.map((row, i) => {
            const isTop3 = i < 3;
            const medalColor = medals[i] || "#666";
            return (
              <div
                key={row.username}
                className="flex items-center gap-4 p-4 border rounded-md"
                style={{
                  borderColor: isTop3 ? `${medalColor}55` : "rgba(255,255,255,0.08)",
                  background: isTop3 ? `${medalColor}0F` : "rgba(0,0,0,0.2)",
                }}
                data-testid={`leaderboard-row-${row.username}`}
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                  style={{
                    background: isTop3 ? `${medalColor}22` : "rgba(255,255,255,0.05)",
                    border: `2px solid ${isTop3 ? medalColor : "rgba(255,255,255,0.1)"}`,
                    color: isTop3 ? medalColor : "#888",
                  }}
                >
                  {isTop3 ? <Award className="w-5 h-5" /> : <span className="sc-mono text-sm font-black">{i + 1}</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="sc-heading text-sm font-bold truncate">@{row.username}</div>
                  <div className="text-[11px] text-zinc-500 sc-mono">
                    {row.last_resolved_at ? `Last closed: ${new Date(row.last_resolved_at).toLocaleDateString()}` : "No resolutions yet"}
                  </div>
                </div>
                <div className="text-right">
                  <div className="sc-heading text-2xl font-black" style={{ color: isTop3 ? medalColor : "#fff" }} data-testid={`leaderboard-row-${row.username}-resolved`}>
                    {row.resolved}
                  </div>
                  <div className="text-[10px] sc-mono uppercase text-zinc-500 tracking-widest">resolved</div>
                </div>
                <div className="text-right border-l border-white/5 pl-4">
                  <div className="sc-heading text-lg font-bold text-[color:var(--sc-cyan)]" data-testid={`leaderboard-row-${row.username}-active`}>
                    {row.active_claims}
                  </div>
                  <div className="text-[10px] sc-mono uppercase text-zinc-500 tracking-widest">active</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
